<template lang="pug">
    .photo-album.mx-auto.content(:class="[spacingTop, spacingBottom, container]" v-if="contents && contents.length")
        .column.preview-img-list(align="center" justify="center" ref="container" :style="{ height: `${layoutGeometry.containerHeight}px` }")
            figure(
                v-for="(box, index) in boxes"
                :style="{ top: `${box.style.top}px`, left: `${box.style.left}px` }"
            )
                AlbumImage(
                    class="preview-img-item"
                    :box="box"
                )
</template>

<script>
import { trim, get, debounce, cloneDeep } from '@/helpers';
import justifiedLayout from 'justified-layout';

import AlbumImage from './AlbumImage';

export default {
    name: 'PhotoAlbum',
    data() {
        return {
            config: {
                containerWidth: undefined,
                containerPadding: {
                    top: undefined,
                    right: undefined,
                    bottom: undefined,
                    left: 200
                },
                boxSpacing: 20,
                targetRowHeight: 250
                // showWidows: false
            }
        };
    },
    props: {
        section: {
            type: Object,
            required: true
        }
    },
    components: {
        AlbumImage
    },
    computed: {
        contents() {
            let contents = this.section.contents || [];
            return cloneDeep(contents).sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0));
        },

        spacingTop() {
            let spacingTop = 'spacingTopStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingTop');
                if (setting && setting.value !== undefined) spacingTop = setting.value;
            }
            return spacingTop;
        },

        spacingBottom() {
            let spacingBottom = 'spacingBottomStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingBottom');
                if (setting && setting.value !== undefined) spacingBottom = setting.value;
            }
            return spacingBottom;
        },

        container() {
            let container = 'container';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'container');
                if (setting && setting.value !== undefined) container = setting.value;
            }
            return container;
        },

        images() {
            let images = [];

            this.contents.forEach(content => {
                const image = get(content, 'images.0');
                if (!image) return false;

                image.title = content.title;
                image.text = content.text;
                image.hyperlink = content.hyperlink;

                images.push(image);
            });

            return images;
        },
        ratios() {
            let ratios = [];

            this.images.forEach(image => {
                const fz = get(image, 'pivot.z') || 1;
                const w = get(image, 'pivot.w') || image.width / fz;
                const h = get(image, 'pivot.h') || image.height / fz;

                const ratio = w / h;
                ratios.push(ratio);
            });

            return ratios;
        },

        layoutGeometry() {
            return justifiedLayout(this.ratios, this.config);
        },

        boxes() {
            let boxes = [];

            const storageRoute = trim(
                `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                '/'
            );

            const calculateFit = (data, maxHeight) => {
                const pivot = data.pivot;
                let fit = get(pivot, 'fit') || 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1.1111111111';

                // let { w, h, x, y, z } = pivot
                let { w, h, z } = pivot;
                if (!z) z = 1;

                let width = data.width / z;
                let height = data.height / z;
                let aspectRatio = width / height;

                maxHeight = Math.min(maxHeight, height);

                if (w && h) {
                    aspectRatio = w / h;

                    if (h > maxHeight) {
                        height = maxHeight;
                    }

                    if (height * aspectRatio > data.width / z) {
                        height = width / aspectRatio;
                    } else {
                        width = height * aspectRatio;
                    }

                    fit = fit
                        .replace(/w=([0-9]+\.?[0-9]*)/, `w=${width}`)
                        .replace(/h=([0-9]+\.?[0-9]*)/, `h=${height}`);
                } else if (!w && !h) {
                    if (height > maxHeight) {
                        height = maxHeight;
                        width = height * aspectRatio;
                    }

                    fit += `&w=${width}`;
                }

                return {
                    src: `${storageRoute}/images/${data.id}/${data.name}?${fit}`,
                    error: `${storageRoute}/images/${data.id}/${data.name}?${fit}&preview=greyscale`,
                    loading: `${storageRoute}/images/${data.id}/${data.name}?${fit}&preview=blur`,
                    width,
                    height,
                    aspectRatio
                };
            };

            this.images.forEach((el, i) => {
                const style = this.layoutGeometry.boxes[i];

                let data, thumbnail, image;
                data = el;

                image = calculateFit(data, 1800);
                thumbnail = calculateFit(data, 500);

                boxes.push({ style, data, thumbnail, image });
            });

            return boxes;
        }
    },
    mounted() {
        const calculateDimansions = () => {
            if (this.$refs.container) {
                const style = getComputedStyle(this.$refs.container);
                this.config.containerWidth = this.$refs.container.offsetWidth;
                // this.config.containerWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)

                this.config.containerPadding = {
                    top: parseInt(style.paddingTop),
                    right: parseInt(style.paddingRight),
                    bottom: parseInt(style.paddingBottom),
                    left: parseInt(style.paddingLeft)
                };
            }
        };

        window.addEventListener('resize', debounce(calculateDimansions, 100));
        calculateDimansions();
    }
};
</script>

<style lang="sass" scoped>

@import "~@/assets/sass/variables"

.fluid
  @apply px-5

.photo-album.content .column
    position: relative
    figure
        background: #eeeeee,
        overflow: hidden
        //border-radius: 4px
        position: absolute

        & ::v-deep > .preview-img-item
            transition: transform 1s cubic-bezier(0, 1, 0.5, 1)

            &:hover
               background-color: $cc-blue

            & > img
                transition: transform 1s cubic-bezier(0, 1, 0.5, 1)
            & > caption
                transition: background .3s cubic-bezier(0, 0.9, 0.31, 1.04), backdrop-filter .3s cubic-bezier(0, 0.9, 0.31, 1.04)
        &:hover ::v-deep > .preview-img-item
            position: relative
            z-index: 2
            //transform: scale(1.1)
            & > img
                box-shadow: $box-shadow-3

            & > caption
                opacity: 0.6
                background: rgba(26,37,65,.3)
                backdrop-filter: blur(2px)
        ::v-deep
            img
                //border-radius: 4px
                display: block
                opacity: 1
                &:hover
                  opacity: 0.5
                &[lazy=loading]
                  box-shadow: none
                &[lazy=loaded]
                    //box-shadow: $box-shadow-2
            caption
                font-size: .8rem
                position: absolute
                left: 0
                bottom: 0
                padding: 10px
                right: 0
                text-align: left
                color: white
                //border-bottom-right-radius: 4px
                //border-bottom-left-radius: 4px
                background: rgba(26,37,65,.8)
                backdrop-filter: blur(6px)
                h4
                    color: inherit
                    margin-bottom: 0
</style>
